import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// All translation namespaces
export const NAMESPACES = [
  'common',
  'knowledge',
  'parts',
  'assets',
  'tools',
  'tickets',
  'teams',
  'calibrations',
  'training',
  'problems',
  'problem-assets',
  'notifications',
  'settings',
  'preventive-maintenance',
  'integrations',
  'parameter-management',
  'conditionBased',
  'fileManager',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'th', 'vi', 'ms', 'id', 'zh', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Language display names with flags
export const LANGUAGE_OPTIONS: Record<SupportedLanguage, { name: string; flag: string }> = {
  en: { name: 'English', flag: '🇺🇸' },
  th: { name: 'ไทย', flag: '🇹🇭' },
  vi: { name: 'Tiếng Việt', flag: '🇻🇳' },
  ms: { name: 'Bahasa Melayu', flag: '🇲🇾' },
  id: { name: 'Bahasa Indonesia', flag: '🇮🇩' },
  zh: { name: '中文', flag: '🇨🇳' },
  es: { name: 'Español', flag: '🇪🇸' },
} as const;

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// Get stored language or detect from browser
export function getInitialLanguage(): SupportedLanguage {
  // Check localStorage first
  const stored = localStorage.getItem('language') as SupportedLanguage;
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored;
  }

  // Check browser language
  const browserLang = navigator.language.split('-')[0] as SupportedLanguage;
  if (SUPPORTED_LANGUAGES.includes(browserLang)) {
    return browserLang;
  }

  return DEFAULT_LANGUAGE;
}

// Load translations for a specific language
async function loadLanguageTranslations(lang: string) {
  try {
    const namespaces = await Promise.all([
      import(`./locales/${lang}/common.json`),
      import(`./locales/${lang}/knowledge.json`).catch(() => null),
      import(`./locales/${lang}/parts.json`).catch(() => null),
      import(`./locales/${lang}/assets.json`).catch(() => null),
      import(`./locales/${lang}/tools.json`).catch(() => null),
      import(`./locales/${lang}/tickets.json`).catch(() => null),
      import(`./locales/${lang}/teams.json`).catch(() => null),
      import(`./locales/${lang}/calibrations.json`).catch(() => null),
      import(`./locales/${lang}/training.json`).catch(() => null),
      import(`./locales/${lang}/problems.json`).catch(() => null),
      import(`./locales/${lang}/problem-assets.json`).catch(() => null),
      import(`./locales/${lang}/notifications.json`).catch(() => null),
      import(`./locales/${lang}/settings.json`).catch(() => null),
      import(`./locales/${lang}/preventive-maintenance.json`).catch(() => null),
      import(`./locales/${lang}/integrations.json`).catch(() => null),
      import(`./locales/${lang}/parameter-management.json`).catch(() => null),
      import(`./locales/${lang}/conditionBased.json`).catch(() => null),
      import(`./locales/${lang}/fileManager.json`).catch(() => null),
    ]);

    const resources: Record<string, any> = {
      common: namespaces[0].default,
    };

    if (namespaces[1]) resources.knowledge = namespaces[1].default;
    if (namespaces[2]) resources.parts = namespaces[2].default;
    if (namespaces[3]) resources.assets = namespaces[3].default;
    if (namespaces[4]) resources.tools = namespaces[4].default;
    if (namespaces[5]) resources.tickets = namespaces[5].default;
    if (namespaces[6]) resources.teams = namespaces[6].default;
    if (namespaces[7]) resources.calibrations = namespaces[7].default;
    if (namespaces[8]) resources.training = namespaces[8].default;
    if (namespaces[9]) resources.problems = namespaces[9].default;
    if (namespaces[10]) resources['problem-assets'] = namespaces[10].default;
    if (namespaces[11]) resources.notifications = namespaces[11].default;
    if (namespaces[12]) resources.settings = namespaces[12].default;
    if (namespaces[13]) resources['preventive-maintenance'] = namespaces[13].default;
    if (namespaces[14]) resources.integrations = namespaces[14].default;
    if (namespaces[15]) resources['parameter-management'] = namespaces[15].default;
    if (namespaces[16]) resources.conditionBased = namespaces[16].default;
    if (namespaces[17]) resources.fileManager = namespaces[17].default;

    return resources;
  } catch (_error) {
    console.warn(`Failed to load ${lang}, falling back to English`);
    // Fallback to English if language loading fails
    return loadLanguageTranslations('en');
  }
}

// Pre-load English translations to avoid Suspense blocking
const initialLanguage = getInitialLanguage();

// Configure i18next with empty resources initially
i18n.use(initReactI18next).init({
  lng: initialLanguage,
  fallbackLng: DEFAULT_LANGUAGE,
  defaultNS: 'common',
  ns: NAMESPACES,
  react: {
    useSuspense: false,
  },
  interpolation: {
    escapeValue: false,
  },
  saveMissing: false,
});

// Load initial language resources
Promise.all([
  loadLanguageTranslations(initialLanguage),
  // Always load English as fallback
  loadLanguageTranslations('en'),
])
  .then(([initialResources, enResources]) => {
    // Add English as fallback
    Object.entries(enResources).forEach(([ns, value]) => {
      i18n.addResourceBundle('en', ns, value);
    });

    // Add initial language resources (if not English)
    if (initialLanguage !== 'en') {
      Object.entries(initialResources).forEach(([ns, value]) => {
        i18n.addResourceBundle(initialLanguage, ns, value);
      });
    }

    // Change to the initial language
    i18n.changeLanguage(initialLanguage);
  })
  .catch((err) => {
    console.error('Failed to load language:', err);
  });

// Change language and persist
export async function changeLanguage(lang: SupportedLanguage): Promise<void> {
  localStorage.setItem('language', lang);

  // If language is not English, load its translations
  if (lang !== 'en' && !i18n.hasResourceBundle(lang, 'common')) {
    const resources = await loadLanguageTranslations(lang);
    Object.entries(resources).forEach(([ns, value]) => {
      i18n.addResourceBundle(lang, ns, value);
    });
  }

  await i18n.changeLanguage(lang);
}

export default i18n;
